import { DonorFrame } from '@components/donor';
import { useCloudinaryFileUploader } from '@hooks/cloudinary';
import { useDisableScroll } from '@hooks/dom';
import { useData } from '@hooks/form';
import { useNotificationChannel } from '@hooks/notification';
import { Grid, Hidden } from '@mui/material';
import { useGetIncentive, useUpdateIncentive } from '@queries/incentives';
import { donorAppRoutes, GivzQueryParams } from '@routes';
import { FunctionComponent } from 'react';
import { SettingsPanel } from './settings-panel';

export type DonationExperienceState = Omit<
  GivzQueryParams,
  | 'amount'
  | 'landingPageHero'
  | 'landingPageLogo'
  | 'donationHideSearchBar'
  | 'donationEmailRequired'
  | 'donationShowUSD'
  | 'landingPageWidgetOnly'
> & {
  donationHideSearchBar?: boolean;
  donationEmailRequired?: boolean;
  donationShowUSD?: boolean;
  landingPageHero?: File;
  landingPageHeroUrl?: string;
  landingPageLogo?: File;
  landingPageLogoUrl?: string;
  landingPageWidgetOnly?: boolean;
  previewUrl: string;
};

const dataToUrl = (slug: string, data: DonationExperienceState) =>
  donorAppRoutes
    .generator({
      queryParams: {
        donationDescription: data.donationDescription,
        donationEmailPrompt: data.donationEmailPrompt,
        donationEmailRequired: Number(data.donationEmailRequired).toString(),
        donationHideSearchBar: Number(data.donationHideSearchBar).toString(),
        donationPrimaryColor: data.donationPrimaryColor,
        donationShowUSD: Number(data.donationShowUSD).toString(),
        donationTitle: data.donationTitle,
        landingPageBackgroundColor: data.landingPageBackgroundColor,
        landingPageDescription: data.landingPageDescription,
        landingPageTitle: data.landingPageTitle,
        landingPageWidgetOnly: Number(data.landingPageWidgetOnly).toString(),
        preview: '1',
      },
      slug,
    })
    .toString();

export type DonationExperienceProps = { gid: string };

export const DonationExperience: FunctionComponent<DonationExperienceProps> = (
  props,
) => {
  const { gid } = props;
  useDisableScroll({
    elementId: 'root',
    mediaQuery: {
      query: 'up',
      start: 'lg',
    },
  });
  const { success } = useNotificationChannel();
  const getIncentiveQuery = useGetIncentive({ body: {}, gid });
  const updateIncentiveMutation = useUpdateIncentive();

  const { fileUploadLoading, uploadFileToCloudinary } =
    useCloudinaryFileUploader();

  function onSuccess() {
    success(`The incentive ${activeIncentive?.gid} was updated.`);
  }

  const activeIncentive = getIncentiveQuery.data?.data;

  const donationExperienceData = useData<DonationExperienceState>({
    donationDescription:
      activeIncentive?.donationWidgetSettings?.description?.value,
    donationEmailPrompt: activeIncentive?.donationWidgetSettings?.emailPrompt,
    donationEmailRequired:
      activeIncentive?.donationWidgetSettings?.emailRequired,
    donationHideSearchBar:
      activeIncentive?.donationWidgetSettings?.hideSearchBar,
    donationShowUSD: activeIncentive?.donationWidgetSettings?.showUSD,
    donationTitle: activeIncentive?.donationWidgetSettings?.title?.value,
    landingPageDescription:
      activeIncentive?.hostedPageSettings?.landingPage?.description?.value,
    landingPageHeroUrl:
      activeIncentive?.hostedPageSettings?.landingPage?.hero?.value,
    landingPageLogoUrl:
      activeIncentive?.hostedPageSettings?.landingPage?.logo?.value,
    landingPageTitle:
      activeIncentive?.hostedPageSettings?.landingPage?.title?.value,
    landingPageWidgetOnly:
      activeIncentive?.hostedPageSettings?.landingPage?.widgetOnly,
    previewUrl: donorAppRoutes
      .generator({
        queryParams: {
          preview: '1',
        },
        slug: activeIncentive?.slug!,
      })
      .toString(),
  });
  const { data, setData, setValueFromKey } = donationExperienceData;

  const onSave = async () => {
    const [heroImage, logoImage] = await Promise.all([
      data.landingPageHero && uploadFileToCloudinary(data.landingPageHero),
      data.landingPageLogo && uploadFileToCloudinary(data.landingPageLogo),
    ]);

    updateIncentiveMutation.mutate(
      {
        gid: activeIncentive?.gid!,
        hostedCampaignGeneratorUpdate: {
          donationWidgetOverrides: {
            description: {
              value: data.donationDescription,
            },
            emailRequired: data.donationEmailRequired,
            hideSearchBar: data.donationHideSearchBar,
            showUSD: data.donationShowUSD,
            title: {
              value: data.donationTitle,
            },
          },
          hostedPageOverrides: {
            landingPage: {
              description: {
                value: data.landingPageDescription,
              },
              title: {
                value: data.landingPageTitle,
              },
              widgetOnly: Boolean(data.landingPageWidgetOnly),
              ...(logoImage && {
                logo: {
                  value: logoImage.secure_url,
                },
              }),
              ...(heroImage && {
                hero: {
                  value: heroImage.secure_url,
                },
              }),
            },
          },
        },
      },
      {
        onSuccess,
      },
    );

    setData((data) => ({
      ...data,
      landingPageHero: undefined,
      landingPageLogo: undefined,
      ...(heroImage && {
        landingPageHeroUrl: heroImage.secure_url,
      }),
      ...(logoImage && {
        landingPageLogoUrl: logoImage.secure_url,
      }),
    }));

    onPreview();
  };

  const onPreview = () => {
    setValueFromKey('previewUrl')(dataToUrl(activeIncentive?.slug!, data));
  };

  return (
    <Grid container rowSpacing={1} columnSpacing={1}>
      <Hidden lgDown>
        <Grid item md={12} sm={12} xs={12} lg={8} xl={8}>
          <DonorFrame url={data.previewUrl} />
        </Grid>
      </Hidden>
      <Grid item md={12} sm={12} xs={12} lg={4} xl={4}>
        <SettingsPanel
          isLoading={updateIncentiveMutation.isLoading || fileUploadLoading}
          useData={donationExperienceData}
          onSave={onSave}
        />
      </Grid>
    </Grid>
  );
};
